import { useMemo, useRef, useState, useEffect } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Environment, Lightformer, Float, Sparkles, RoundedBox, PerformanceMonitor } from "@react-three/drei";
import * as THREE from "three";
import LogoMark3D from "./LogoMark3D";

const WORLD_DEPTH = 46;

const Chrome = ({ color = "#f5f5f5", ...p }) => (
  <meshStandardMaterial color={color} metalness={1} roughness={0.14} envMapIntensity={1.4} {...p} />
);

const Glass = (p) => (
  <meshStandardMaterial
    color="#ffffff"
    metalness={0.85}
    roughness={0.06}
    transparent
    opacity={0.22}
    envMapIntensity={1.5}
    depthWrite={false}
    {...p}
  />
);

const BrowserFrame = ({ position, scale = 1 }) => (
  <Float speed={1.1} rotationIntensity={0.35} floatIntensity={0.9}>
    <group position={position} scale={scale}>
      <RoundedBox args={[1.9, 1.15, 0.05]} radius={0.05} smoothness={4}>
        <meshStandardMaterial color="#0a0a0a" metalness={0.7} roughness={0.3} transparent opacity={0.8} envMapIntensity={1.2} />
      </RoundedBox>
      <mesh position={[0, 0.46, 0.03]}>
        <boxGeometry args={[1.7, 0.09, 0.01]} />
        <meshBasicMaterial color="#ffffff" transparent opacity={0.35} />
      </mesh>
      {[-0.72, -0.58, -0.44].map((x, i) => (
        <mesh key={i} position={[x, 0.46, 0.035]}>
          <circleGeometry args={[0.025, 12]} />
          <meshBasicMaterial color="#ffffff" transparent opacity={0.7} />
        </mesh>
      ))}
      <mesh position={[0, -0.05, 0.032]}>
        <planeGeometry args={[1.5, 0.68]} />
        <meshBasicMaterial color="#ffffff" transparent opacity={0.07} />
      </mesh>
    </group>
  </Float>
);

const FloatingShape = ({ kind, position, scale = 1, glass = false }) => (
  <Float speed={1 + Math.random()} rotationIntensity={0.6} floatIntensity={1.1}>
    <mesh position={position} scale={scale}>
      {kind === "ico" && <icosahedronGeometry args={[0.5, 0]} />}
      {kind === "sphere" && <sphereGeometry args={[0.42, 32, 32]} />}
      {kind === "torus" && <torusGeometry args={[0.42, 0.15, 16, 48]} />}
      {kind === "box" && <boxGeometry args={[0.6, 0.6, 0.6]} />}
      {kind === "octa" && <octahedronGeometry args={[0.5, 0]} />}
      {glass ? <Glass /> : <Chrome />}
    </mesh>
  </Float>
);

const WorldContent = ({ tier, pointer }) => {
  const world = useRef();
  const heroLogo = useRef();
  const ctaLogo = useRef();
  const camTarget = useMemo(() => new THREE.Vector3(), []);
  const { size } = useThree();
  const narrow = size.width < 768;

  const shapes = useMemo(() => {
    const kinds = ["ico", "sphere", "torus", "box", "octa"];
    const count = tier === "high" ? 14 : tier === "medium" ? 9 : 5;
    return Array.from({ length: count }, (_, i) => ({
      kind: kinds[i % kinds.length],
      position: [
        (i % 2 ? 1 : -1) * (2.8 + Math.abs(Math.sin(i * 2.4)) * 3.2),
        -((i + 1) * (WORLD_DEPTH / count)) + 2,
        -3.5 - (i % 4) * 1.6,
      ],
      scale: 0.5 + ((i * 7) % 10) / 14,
      glass: tier === "high" ? i % 4 === 0 : false,
    }));
  }, [tier]);

  useFrame((state, delta) => {
    const t = state.clock.elapsedTime;
    const doc = document.documentElement;
    const max = Math.max(doc.scrollHeight - window.innerHeight, 1);
    const progress = Math.min(Math.max(window.scrollY / max, 0), 1);

    if (world.current) {
      world.current.position.y = THREE.MathUtils.damp(world.current.position.y, progress * WORLD_DEPTH, 2.2, delta);
      world.current.rotation.z = THREE.MathUtils.damp(world.current.rotation.z, progress * 0.14, 2, delta);
    }
    if (heroLogo.current) {
      heroLogo.current.rotation.y += delta * 0.22;
      heroLogo.current.position.y = Math.sin(t * 0.5) * 0.18 + (narrow ? -2.9 : -1.9);
    }
    if (ctaLogo.current) {
      ctaLogo.current.rotation.y += delta * 0.16;
      ctaLogo.current.rotation.x = Math.sin(t * 0.3) * 0.12;
    }
    const px = pointer.current.x * 0.7;
    const py = pointer.current.y * 0.45;
    state.camera.position.x = THREE.MathUtils.damp(state.camera.position.x, px, 2.5, delta);
    state.camera.position.y = THREE.MathUtils.damp(state.camera.position.y, py, 2.5, delta);
    camTarget.set(0, 0, 0);
    state.camera.lookAt(camTarget);
  });

  return (
    <>
      <group ref={world}>
        <group ref={heroLogo} position={[narrow ? 0 : 3.1, -2.2, narrow ? -5 : -3.5]}>
          <LogoMark3D scale={narrow ? 1.35 : 2.2} glass={tier !== "low"} />
        </group>
        <Float speed={0.8} rotationIntensity={0.3} floatIntensity={0.8}>
          <LogoMark3D position={[-4.6, -17, -5]} scale={0.9} glass={tier === "high"} />
        </Float>
        <Float speed={0.9} rotationIntensity={0.3} floatIntensity={0.8}>
          <LogoMark3D position={[4.8, -26, -5.5]} scale={0.7} glass={tier === "high"} />
        </Float>
        <group ref={ctaLogo} position={[0, -WORLD_DEPTH - 1.5, narrow ? -5 : -3.5]}>
          <LogoMark3D scale={narrow ? 1.6 : 3} glass={tier !== "low"} />
        </group>
        {shapes.map((s, i) => (
          <FloatingShape key={i} {...s} />
        ))}
        <BrowserFrame position={[4.2, -7.5, -4]} scale={1.1} />
        <BrowserFrame position={[-4.8, -13.5, -4.5]} scale={0.9} />
        <BrowserFrame position={[4.6, -32, -4]} scale={1} />
        <BrowserFrame position={[-4.2, -39, -5]} scale={1.2} />
        <Sparkles
          count={tier === "high" ? 90 : tier === "medium" ? 50 : 25}
          scale={[16, WORLD_DEPTH + 14, 10]}
          position={[0, -WORLD_DEPTH / 2, -3]}
          size={2.4}
          speed={0.25}
          color="#ffffff"
          opacity={0.55}
        />
      </group>
      <ambientLight intensity={0.25} />
      <spotLight position={[6, 8, 6]} angle={0.5} penumbra={1} intensity={140} color="#ffffff" />
      <spotLight position={[-7, -6, 4]} angle={0.6} penumbra={1} intensity={90} color="#dcdcdc" />
      <Environment resolution={256} frames={1}>
        <Lightformer intensity={5} position={[0, 5, -6]} scale={[12, 4, 1]} color="#ffffff" />
        <Lightformer intensity={3} position={[-6, 2, 2]} rotation-y={Math.PI / 2} scale={[8, 2.5, 1]} color="#e8e8e8" />
        <Lightformer intensity={3.4} position={[6, -1, 2]} rotation-y={-Math.PI / 2} scale={[8, 2.5, 1]} color="#ffffff" />
        <Lightformer intensity={2} position={[0, -6, 3]} scale={[10, 3, 1]} color="#d4d4d4" />
        <Lightformer intensity={2.6} position={[0, 2, 5]} scale={[6, 1.5, 1]} color="#ffffff" />
      </Environment>
      <fog attach="fog" args={["#050505", 7, 22]} />
    </>
  );
};

const Environment3D = ({ tier, reducedMotion }) => {
  const pointer = useRef({ x: 0, y: 0 });
  const [dpr, setDpr] = useState(tier === "high" ? 1.25 : 1);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const onVis = () => setHidden(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  if (reducedMotion) {
    return (
      <div
        className="fixed inset-0 z-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 45% at 50% 12%, rgba(255,255,255,0.09), transparent 70%), #050505",
        }}
      />
    );
  }

  return (
    <div
      className="fixed inset-0 z-0"
      onPointerMove={(e) => {
        pointer.current.x = (e.clientX / window.innerWidth) * 2 - 1;
        pointer.current.y = -((e.clientY / window.innerHeight) * 2 - 1);
      }}
    >
      <Canvas
        dpr={dpr}
        frameloop={hidden ? "never" : "always"}
        camera={{ position: [0, 0, 8], fov: 46 }}
        gl={{ antialias: tier === "high", alpha: true, powerPreference: "high-performance", stencil: false, depth: true }}
        style={{ pointerEvents: "none" }}
      >
        <PerformanceMonitor
          onDecline={() => setDpr((d) => Math.max(0.75, d - 0.25))}
          flipflops={3}
          onFallback={() => setDpr(0.75)}
        />
        <WorldContent tier={tier} pointer={pointer} />
      </Canvas>
    </div>
  );
};

export default Environment3D;
